import React from 'react';
import './ConsultantLanding.scss';
import { FormattedMessageHelper } from '../../shared/helpers/internationalization';
import { LEARN_STARTER_PACK_PATH, SIGN_UP } from '../../shared/constants';
import { useSelector } from 'react-redux';
import { getIsLoggedInSelector } from '../../reducers/AuthenticationReducer/AuthenticationSelector';
import { useHistory } from 'react-router-dom';
import { PUBLIC_URL } from '../../config';
import { Helmet } from 'react-helmet';
import { injectIntl, IntlShape } from 'react-intl';
import analytics from '../../shared/services/analytics/analytics';
import { List } from 'semantic-ui-react';
import { ReactVideoVidyard, ReactVideoYoutube } from '../../components/Learn/Courses/CourseLesson';
import noop from 'lodash/noop';
import { addModifiers } from '../../shared/helpers';
import { FeaturesSection } from './FeatureSection';
import { LanguageSelector } from '../IQCLanding/LanguageSelector';
import moment from 'moment';

const REDIRECT_URL = `${PUBLIC_URL}${SIGN_UP}`;

const ConsultantLandingWithoutIntl = ({ intl }: { intl: IntlShape }) => {
  const isLoggedIn = useSelector(getIsLoggedInSelector);
  const history = useHistory();

  const onJoinClick = (event_label: string) => () => {
    analytics.trackCustomEvent('cl_click_join', { event_label });
    history.push(REDIRECT_URL);
  };
  const year = moment().format('YYYY');

  return (
    <div className="consultant-landing">
      <Helmet>
        <title>WorldQuant BRAIN Research Consultant Program</title>
        <meta
          name="description"
          content="Join one of the world’s largest global quant finance communities!
Leverage our data and technology on our WorldQuant BRAIN simulation platform to build advanced Alphas and contribute to WorldQuant’s overall research effort."
        />
        <meta property="og:title" content="WorldQuant BRAIN Research Consultant Program" />
        <meta
          property="og:description"
          content="Join one of the world’s largest global quant finance communities!
Leverage our data and technology on our WorldQuant BRAIN simulation platform to build advanced Alphas and contribute to WorldQuant’s overall research effort."
        />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        {/*@ts-ignore*/}
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin />
        <link
          href="https://fonts.googleapis.com/css2?family=Nunito+Sans:ital,wght@0,200;0,300;0,400;0,700;1,300&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <div className="consultant-landing__language-selector-wrapper consultant-landing__language-selector-wrapper--desktop">
        <div className="consultant-landing__language-selector-inner-wrapper">
          <LanguageSelector className="consultant-landing__language-selector" />
        </div>
      </div>
      <JoinCommunitySection isLoggedIn={isLoggedIn} onJoinClick={onJoinClick} />
      <FeaturesSection isLoggedIn={isLoggedIn} />
      <GeniusProgramSection isLoggedIn={isLoggedIn} />
      {isLoggedIn ? <EligibleUsers /> : <StartJourneySection onJoinClick={onJoinClick} />}
      {isLoggedIn && <MeetConsultantsSection />}
      <div className="consultant-landing__language-selector-wrapper consultant-landing__language-selector-wrapper--mobile">
        <LanguageSelector isUp={true} className="consultant-landing__language-selector" />
      </div>
      <div className="consultant-landing__section">
        <div className="consultant-landing__section-container">
          <footer className="consultant-landing__footer">
            <div className="consultant-landing__copyright">
              <div className="consultant-landing__copyright-text">
                <FormattedMessageHelper id={'consultantLanding.copyrightText'} values={{ year }} />
              </div>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
};

export const ConsultantLanding = injectIntl(ConsultantLandingWithoutIntl);

interface JoinCommunitySectionParams {
  isLoggedIn: boolean;
  onJoinClick: (event_label: string) => () => void;
}

interface VideoContainerProps {
  modifier: string;
  isLoggedIn: boolean;
}
function VideoContainer({ modifier, isLoggedIn }: VideoContainerProps) {
  if (isLoggedIn) {
    const videoId = '3C7YtuL2HRiM3GEz6BQvex';
    const loggedInModifier = 'logged-in';

    return (
      <div className={addModifiers('consultant-landing__video-container', [modifier, loggedInModifier])}>
        <div className="consultant-landing__video">
          <h2 className="consultant-landing__video-name">
            <FormattedMessageHelper id={isLoggedIn ? 'consultantLanding.videoTitle.logged' : 'consultantLanding.videoTitle'} />
          </h2>
          <ReactVideoVidyard uuid={videoId} handlePlayNextVideo={noop} autoPlay={'0'} modifiers={['consultant-landing']} />
        </div>
      </div>
    );
  } else {
    const videoId = 'QjhqRrPQs2Q';
    const loggedInModifier = 'logged-out';

    return (
      <div className={addModifiers('consultant-landing__video-container', [modifier, loggedInModifier])}>
        <div className="consultant-landing__video">
          <h2 className="consultant-landing__video-name">
            <FormattedMessageHelper id={isLoggedIn ? 'consultantLanding.videoTitle.logged' : 'consultantLanding.videoTitle'} />
          </h2>
          <ReactVideoYoutube uuid={videoId} modifiers={['consultant-landing']} autoPlay={'0'} />
        </div>
      </div>
    );
  }
}

function JoinCommunitySection({ isLoggedIn, onJoinClick }: JoinCommunitySectionParams) {
  const joinHeaderId = isLoggedIn ? 'consultantLanding.joinHeader.logged' : 'consultantLanding.joinHeader';

  return (
    <div className="consultant-landing__section">
      <div className="consultant-landing__section-container">
        <div className="consultant-landing__sphere"></div>
        <div className="consultant-landing__hero">
          <div className="consultant-landing__outer">
            <h1 className="consultant-landing__title">
              <FormattedMessageHelper id={isLoggedIn ? 'consultantLanding.title.logged' : 'consultantLanding.title'} />
            </h1>
            <h2
              className={`consultant-landing__title ${addModifiers(
                'consultant-landing__title',
                isLoggedIn ? ['h2', 'logged-in'] : ['h2']
              )}`}
            >
              <FormattedMessageHelper id={joinHeaderId} />
            </h2>
          </div>
          <VideoContainer modifier="large" isLoggedIn={isLoggedIn} />
        </div>
        <div className="consultant-landing__section-content">
          <p className="consultant-landing__paragraph">
            <FormattedMessageHelper id="consultantLanding.leverageOurData" />
            <span className="consultant-landing__paragraph--extra">
              <FormattedMessageHelper
                id="consultantLanding.leverageOurData.asterix"
                values={{ asterisk: <i className="consultant-landing__" /> }}
              />
            </span>
          </p>
          <div className="consultant-landing__stats_group">
            <div className="consultant-landing__stat">
              <div className="consultant-landing__stat-number">
                <FormattedMessageHelper id="consultantLanding.userCount.number" />
              </div>
              <div className="consultant-landing__stat-name">
                <FormattedMessageHelper id="consultantLanding.userCount" />
              </div>
            </div>
            <div className="consultant-landing__stat">
              <div className="consultant-landing__stat-number">
                <FormattedMessageHelper id="consultantLanding.consultantCount.number" />
              </div>
              <div className="consultant-landing__stat-name">
                <FormattedMessageHelper id="consultantLanding.consultantCount" />
              </div>
            </div>
            <div className="consultant-landing__stat">
              <div className="consultant-landing__stat-number">
                <FormattedMessageHelper id="consultantLanding.countriesCount.number" />
              </div>
              <div className="consultant-landing__stat-name">
                <FormattedMessageHelper id="consultantLanding.countriesCount" />
              </div>
            </div>
          </div>
          {!isLoggedIn && (
            <button onClick={onJoinClick('top')} className="consultant-landing__join-button">
              <FormattedMessageHelper id="consultantLanding.joinUs" />
            </button>
          )}
          <p className="consultant-landing__paragraph">
            <FormattedMessageHelper id="consultantLanding.availableInCountries" />
            <span className="consultant-landing__accented-text">
              <FormattedMessageHelper id="consultantLanding.countryList" />
            </span>
          </p>
        </div>
        <VideoContainer modifier="small" isLoggedIn={isLoggedIn} />

        <div className="consultant-landing__section-divider"></div>
      </div>
    </div>
  );
}

interface GeniusProgramSectionParams {
  isLoggedIn: boolean;
}

function GeniusProgramSection({ isLoggedIn }: GeniusProgramSectionParams) {
  return (
    <div className={addModifiers('consultant-landing__section', ['genius'])}>
      <div className="consultant-landing__brain-background"></div>
      <div className="consultant-landing__section-container">
        <div className="consultant-landing__inner">
          <div className="consultant-landing__genius-title-container">
            <h2
              className={`consultant-landing__title ${addModifiers(
                'consultant-landing__title',
                isLoggedIn ? ['h2', 'logged-in'] : ['h2']
              )}`}
            >
              <FormattedMessageHelper id="consultantLanding.geniusTitle" />
            </h2>
            <div className="consultant-landing__genius-logo"></div>
          </div>
          <div className="consultant-landing__genius-content">
            <div className="consultant-landing__genius-text">
              <p>
                <FormattedMessageHelper id={'consultantLanding.geniusText'} />
              </p>
            </div>
            <div className="consultant-landing__genius-ladder"></div>
          </div>
        </div>

        <div className="consultant-landing__section-content"></div>

        <div className="consultant-landing__section-divider"></div>
      </div>
    </div>
  );
}

interface StartJourneySectionProps {
  onJoinClick: (event_label: string) => () => void;
}
function StartJourneySection({ onJoinClick }: StartJourneySectionProps) {
  return (
    <div className="consultant-landing__section">
      <div className="consultant-landing__section-container">
        <div className="consultant-landing__sphere"></div>
        <div className="consultant-landing__outer">
          <div className="consultant-landing__section-content">
            <h2 className="consultant-landing__title consultant-landing__title--h2">
              <FormattedMessageHelper id="consultantLanding.startJourney" />
            </h2>
            <div className="consultant-landing__step">
              <h3 className="consultant-landing__title consultant-landing__title--h3">
                <FormattedMessageHelper id="consultantLanding.step1" />
              </h3>
              <p className="consultant-landing__paragraph">
                <FormattedMessageHelper id="consultantLanding.step1.text" />
              </p>

              <button onClick={onJoinClick('bottom')} className="consultant-landing__join-button consultant-landing__join-button--xl">
                <FormattedMessageHelper id="consultantLanding.joinUs" />
              </button>
            </div>

            <div className="consultant-landing__step">
              <h3 className="consultant-landing__title consultant-landing__title--h3">
                <FormattedMessageHelper id="consultantLanding.step2" />
              </h3>
              <p className="consultant-landing__paragraph">
                <FormattedMessageHelper id="consultantLanding.step2.text" />
              </p>
            </div>

            <div className="consultant-landing__step">
              <h3 className="consultant-landing__title consultant-landing__title--h3">
                <FormattedMessageHelper id="consultantLanding.step3" />
              </h3>
              <p className="consultant-landing__paragraph">
                <FormattedMessageHelper id="consultantLanding.step3.text" />
              </p>
            </div>

            <div className="consultant-landing__step">
              <h3 className="consultant-landing__title consultant-landing__title--h3">
                <FormattedMessageHelper id="consultantLanding.step4" />
              </h3>
              <p className="consultant-landing__paragraph">
                <FormattedMessageHelper id="consultantLanding.step4.text" />
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EligibleUsers() {
  return (
    <div className="consultant-landing__section">
      <div className="consultant-landing__section-container">
        <div className="consultant-landing__sphere"></div>
        <div className="consultant-landing__outer">
          <div className="consultant-landing__section-content">
            <h2 className="consultant-landing__title consultant-landing__title--h2">
              <FormattedMessageHelper id="consultantLanding.eligibleHeader" />
            </h2>
            <div className="consultant-landing__path-container">
              <div className="consultant-landing__path-image"></div>
              <List bulleted>
                <List.Item>
                  <FormattedMessageHelper id="consultantLanding.points" />
                  <List.List>
                    <List.Item>
                      <FormattedMessageHelper id="consultantLanding.earnBySubmitting" />
                    </List.Item>
                    <List.Item>
                      <FormattedMessageHelper id="consultantLanding.ruleOfThumb" />
                    </List.Item>
                  </List.List>
                </List.Item>

                <List.Item>
                  <FormattedMessageHelper id="consultantLanding.backgroundCheck" />
                </List.Item>
              </List>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MeetConsultantsSection() {
  const onButtonClick = () => {
    analytics.trackCustomEvent('cl_click_starer_pack');
    window.open(process.env.PUBLIC_URL + LEARN_STARTER_PACK_PATH, '_blank');
  };
  return (
    <div className="consultant-landing__section">
      <div className="consultant-landing__section-container">
        <div className="consultant-landing__sphere"></div>
        <div className="consultant-landing__outer">
          <div className="consultant-landing__section-content">
            <h2 className="consultant-landing__title consultant-landing__title--h2">
              <FormattedMessageHelper id="consultantLanding.meetConsultants" />
            </h2>

            <button onClick={onButtonClick} className="consultant-landing__join-button consultant-landing__join-button--xl">
              <FormattedMessageHelper id="consultantLanding.openStarterPack" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
